import type { ChannelPreviewRow, ChannelTarget } from '@/api/pricing'
import { GrundartikelLink } from '@/components/catalog/GrundartikelLink'
import { fmtEUR, priceDelta } from '@/lib/pricing-format'
import { a2SperreText } from '@/lib/a2-sperre'

interface Props {
  rows: ChannelPreviewRow[]
  loading?: boolean
  /** Optionaler Entwurfs-Edit PRO Kanal-Ziel (schreibt den jeweiligen price_draft). */
  renderDraftCell?: (row: ChannelPreviewRow, target: ChannelTarget) => React.ReactNode
  /** Optionaler Strategie-Umschalter PRO Kanal-Ziel (Marge ⇄ Break-Even). */
  onToggleStrategy?: (row: ChannelPreviewRow, target: ChannelTarget) => void
  /** Optionale Portal-Kennung PRO Kanal-Ziel (online/offline + Link). */
  renderChannelListings?: (row: ChannelPreviewRow, target: ChannelTarget) => React.ReactNode
}

/**
 * Kanal-aware Vorschau-Tabelle: eine Zeile pro Variante, je aktivem Kanal
 * (base/amazon) Politik, kanal-eigener Ist-Preis, Zielpreis, Δ gegen den
 * Ist-Preis DIESES Kanals, BE-Badge + Strategie-Toggle und Entwurfsfeld.
 */
export function ChannelPreviewTable({ rows, loading = false, renderDraftCell, onToggleStrategy, renderChannelListings }: Props) {
  if (loading) {
    return (
      <div className="rounded-md border border-line bg-panel py-8 text-center text-ink-2">
        Berechne Vorschau…
      </div>
    )
  }
  if (rows.length === 0) {
    return (
      <div className="rounded-md border border-line bg-panel py-8 text-center text-ink-2">
        Keine Vorschau — Grundartikel unten auswählen und „Vorschau berechnen" klicken.
      </div>
    )
  }
  return (
    <div className="rounded-md border border-line bg-panel overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-line-2 text-xs uppercase text-muted">
            <th className="text-left font-medium px-3 py-2">Variante</th>
            <th className="text-right font-medium px-2 py-2">EK (Set)</th>
            <th className="text-right font-medium px-2 py-2">akt. VK</th>
            <th className="text-right font-medium px-2 py-2">Versand</th>
            <th className="text-left font-medium px-2 py-2">Kanal / Politik / Ist / Zielpreis / Entwurf</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.sku} className="border-b border-line-2 last:border-0 align-top hover:bg-panel-2">
              <td className="px-3 py-2">
                <div className="text-sm text-ink truncate max-w-[260px]">{r.name}</div>
                <div className="text-[11px] font-mono text-muted">
                  {/* Eigene DR-Lagernummer der Zeile = eindeutige Identität (Fallback: interne SKU).
                      Verhindert, dass ein Kind mit Stern-SKU wie ein Grundartikel aussieht. */}
                  <span className="text-ink-2 font-medium">{r.dr_stock_id ? `DR-${r.dr_stock_id}` : r.sku}</span>
                  {r.merchant_sku && (
                    <>
                      {/* Sternnummer = operative Hauptnummer (familienweit) */}
                      <span className="text-muted-2"> · </span>
                      {r.set_quantity != null && <span className="text-ink-2">{r.set_quantity} x </span>}
                      <GrundartikelLink code={r.merchant_sku} className="text-primary" />
                    </>
                  )}
                  {r.is_grundartikel ? (
                    // Die Zeile ist der Grundartikel selbst — deutlich als solcher gekennzeichnet
                    <span className="ml-1.5 px-1 py-0.5 rounded bg-panel-2 text-[10px] font-semibold not-italic text-ink-2">
                      Grundartikel
                    </span>
                  ) : (
                    // Kind: Grundartikel-Referenz explizit beschriftet (nicht mehr nur „(DR-…)")
                    <span className="text-muted-2"> · Grundartikel {r.parent_sku}</span>
                  )}
                  {r.calc.bumped && <span className="text-warn"> · hochgestuft</span>}
                </div>
              </td>
              <td className="px-2 py-2 text-right font-mono">
                {fmtEUR(r.ek)}
                {r.set_quantity != null && r.set_quantity > 0 && (
                  // Set-EK-Aufloesung: „4 x 0,53 €" (Einzel-EK = Set-EK / Menge; Ein-Komponenten-Set exakt)
                  <div className="text-[11px] text-muted">{r.set_quantity} x {fmtEUR(r.ek / r.set_quantity)}</div>
                )}
              </td>
              <td className="px-2 py-2 text-right font-mono">{fmtEUR(r.current_vk)}</td>
              <td className="px-2 py-2 text-right font-mono">{fmtEUR(r.calc.shipping)}</td>
              <td className="px-2 py-2">
                {r.targets.length === 0 ? (
                  <span className="text-muted-2 text-xs">kein aktiver Kanal (cia)</span>
                ) : (
                  <div className="space-y-1">
                    {r.targets.map((t) => {
                      const delta = t.current_price != null && t.price != null ? priceDelta(t.current_price, t.price) : null
                      const deltaColor = delta == null ? '' : delta.dir === 'up' ? 'text-success' : delta.dir === 'down' ? 'text-coral' : 'text-muted'
                      return (
                        <div key={t.target} className="flex items-center gap-2 flex-wrap">
                          <span className="px-1.5 py-0.5 rounded bg-panel-2 text-[11px] font-mono w-14 text-center">
                            {t.target}
                          </span>
                          {t.strategy === 'break_even' && (
                            <span className="px-1 py-0.5 rounded bg-warn/20 text-warn text-[10px] font-semibold">BE</span>
                          )}
                          <span className="text-xs text-muted w-20">
                            {t.policy === 'inkl' ? 'inkl. Versand' : t.kundenversand != null ? `zzgl. ${fmtEUR(t.kundenversand)} Versand` : 'zzgl. Versand'}
                          </span>
                          <span className="font-mono text-xs text-muted">
                            {t.current_price != null ? `akt. ${fmtEUR(t.current_price)}` : 'akt. —'}
                          </span>
                          {t.price === null ? (
                            <span className="text-xs text-ink-2" title={a2SperreText(t.sperre) ?? undefined}>— {a2SperreText(t.sperre) ?? 'kein Ziel'}</span>
                          ) : (
                            <span className="font-mono text-sm font-semibold">{fmtEUR(t.price)}</span>
                          )}
                          {delta != null ? (
                            <span className={`font-mono text-xs ${deltaColor}`}>
                              {delta.dir === 'flat' ? '±0' : `${delta.dir === 'up' ? '+' : '−'}${fmtEUR(Math.abs(delta.abs))}`}
                            </span>
                          ) : (
                            <span className="font-mono text-xs text-muted-2">Δ —</span>
                          )}
                          {onToggleStrategy && (
                            <button
                              onClick={() => onToggleStrategy(r, t)}
                              title="Strategie dieses Kanals umschalten (Marge ⇄ Break-Even)"
                              className="px-1.5 py-0.5 rounded border border-line text-[10px] text-ink-2 hover:bg-panel-2"
                            >
                              {t.strategy === 'break_even' ? '→ Marge' : '→ BE'}
                            </button>
                          )}
                          {renderChannelListings && renderChannelListings(r, t)}
                          {renderDraftCell && <span className="ml-auto">{renderDraftCell(r, t)}</span>}
                          {t.warning && (
                            <span className="basis-full text-[11px] text-warn">
                              ⚠ Break-Even wirkt nur auf inkl-Kanäle — exkl-Kanal behält den Margen-Preis.
                            </span>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
